import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { BookOpenCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 0 (red) -> 6 (green), matching the original rubric colour ramp
const LEVEL_COLORS = ['#d64545', '#e07b39', '#e8a13a', '#e6c34a', '#b9c94a', '#8fbf4f', '#5aab3d'];

const CategoryCard = ({ cat }) => (
  <Card className="dashboard-card" data-testid={`rubric-cat-${cat.category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
    <CardContent className="p-5">
      <h3 className="font-bold text-[#1A1A1A] text-lg mb-3">{cat.category}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-2">
        {cat.levels.map((desc, i) => (
          <div key={i} className="rounded-lg border border-slate-200 overflow-hidden flex flex-col">
            <div className="px-2 py-1 text-center font-bold text-white text-sm" style={{ backgroundColor: LEVEL_COLORS[i] }}>
              {i}
            </div>
            <p className="p-2 text-[12px] leading-snug text-[#1A1A1A] flex-1">{desc}</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default function RubricPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const [data, setData] = useState(null);
  const [section, setSection] = useState('tasks');

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/rubric`).then((r) => setData(r.data)).catch(() => {});
    if (new URLSearchParams(window.location.search).get('tab') === 'soft-skills') setSection('skills');
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-[#5C5C5C]" data-testid="rubric-no-access">
        The Scoring Rubric is available to administrators only.
      </div>
    );
  }

  if (!data) {
    return <div className="p-10 text-center text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading rubric…</div>;
  }

  return (
    <div className="space-y-6" data-testid="rubric-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A] flex items-center gap-2">
          <BookOpenCheck className="w-7 h-7 text-[#34657f]" /> Jobsite Report Scoring Rubric
        </h1>
        <p className="text-[#5C5C5C] mt-1">How to score each 0–6 rating on the Daily Jobsite Report.</p>
      </div>

      <Card className="dashboard-card bg-[#f0f5f8]">
        <CardContent className="p-5">
          <h2 className="font-semibold text-[#1A1A1A] mb-2">Quick guide</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            {[data.levels.slice(0, 4), data.levels.slice(4)].map((col, ci) => (
              <div key={ci} className="space-y-1">
                {col.map((l) => (
                  <p key={l.value}>
                    <span className="inline-block w-6 h-6 rounded text-center text-white font-bold mr-2" style={{ backgroundColor: LEVEL_COLORS[l.value] }}>{l.value}</span>
                    {l.label}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 gap-1" role="tablist" aria-label="Rubric sections">
        {[{ id: 'tasks', label: 'Job Task Performance' }, { id: 'skills', label: 'Soft Skills' }].map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={section === t.id}
            onClick={() => setSection(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              section === t.id ? 'bg-[#34657f] text-white' : 'text-[#5C5C5C] hover:text-[#1A1A1A]'
            }`}
            data-testid={`rubric-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {section === 'tasks' && (
        <section className="space-y-4" data-testid="rubric-section-tasks">
          <h2 className="text-xl font-bold text-[#34657f]">Job Task Performance</h2>
          {data.rubric.task_performance.map((cat) => <CategoryCard key={cat.category} cat={cat} />)}
        </section>
      )}

      {section === 'skills' && (
        <section className="space-y-4" data-testid="rubric-section-skills">
          <h2 className="text-xl font-bold text-[#34657f]">Soft Skills</h2>
          {data.rubric.soft_skills.map((cat) => <CategoryCard key={cat.category} cat={cat} />)}
        </section>
      )}
    </div>
  );
}
